import React, { useRef, useLayoutEffect, useState, useEffect } from "react";

const CustomTextareaWidget = (props) => {
  const {
    id,
    options = {},
    value,
    required,
    disabled,
    readonly,
    autofocus,
    onChange,
    onBlur,
    onFocus,
    placeholder,
    uiSchema,
  } = props;

  const textareaRef = useRef(null);
  const debounceRef = useRef(null);
  const caretPosRef = useRef(0);
  const isFocusedRef = useRef(false);
  const pendingValueRef = useRef(null);

  const rawMaxLength = uiSchema?.["ui:maxLength"];
  const maxLength =
    typeof rawMaxLength === "number" && Number.isFinite(rawMaxLength) && rawMaxLength >= 0 ? rawMaxLength : null;
  const hasMaxLength = maxLength !== null;
  const counterId = `${id}-counter`;
  const [charsTyped, setCharsTyped] = useState(hasMaxLength ? String(value ?? "").length : null);

  useEffect(() => {
    if (hasMaxLength) {
      setCharsTyped(String(value ?? "").length);
      return;
    }

    setCharsTyped(null);
  }, [value, hasMaxLength]);

  const [internalValue, setInternalValue] = useState(
    typeof value === "undefined" || value === null ? "" : String(value)
  );

  useEffect(() => {
    if (isFocusedRef.current) {
      return;
    }

    const normalized = typeof value === "undefined" || value === null ? "" : String(value);
    setInternalValue((prev) => (prev === normalized ? prev : normalized));
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceRef.current && pendingValueRef.current !== null) {
        onChange(pendingValueRef.current === "" ? options.emptyValue : pendingValueRef.current);
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [onChange, options.emptyValue]);

  useLayoutEffect(() => {
    if (isFocusedRef.current && textareaRef.current && document.activeElement === textareaRef.current) {
      const valueLength = String(internalValue ?? "").length;
      const pos = Math.min(caretPosRef.current, valueLength);
      try {
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;
        textareaRef.current.setSelectionRange(pos, pos);
        window.scrollTo(scrollX, scrollY);
      } catch (e) {
        // setSelectionRange may fail in some environments
      }
    }
  }, [internalValue]);

  const handleChange = (event) => {
    const val = event.target.value;

    if (hasMaxLength && val.length > maxLength) {
      return;
    }

    const nextCaretPos = event.target.selectionStart ?? val.length;
    caretPosRef.current = nextCaretPos;
    pendingValueRef.current = val;

    setInternalValue(val);

    if (hasMaxLength) {
      setCharsTyped(val.length);
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      pendingValueRef.current = null;
      onChange(val === "" ? options.emptyValue : val);
    }, 500);
  };

  const handleBlur = (event) => {
    isFocusedRef.current = false;
    if (debounceRef.current && pendingValueRef.current !== null) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
      const latestValue = pendingValueRef.current;
      pendingValueRef.current = null;
      onChange(latestValue === "" ? options.emptyValue : latestValue);
    }
    if (onBlur) {
      onBlur(id, event.target.value);
    }
  };

  const handleFocus = (event) => {
    isFocusedRef.current = true;
    caretPosRef.current = event.target.selectionStart || 0;
    if (onFocus) {
      onFocus(id, event.target.value);
    }
  };

  return (
    <div>
      <textarea
        ref={textareaRef}
        id={id}
        className="form-control"
        value={internalValue}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        readOnly={readonly}
        autoFocus={autofocus}
        rows={options.rows || 5}
        aria-describedby={hasMaxLength ? counterId : undefined}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        data-gramm="false"
        data-gramm_editor="false"
        data-enable-grammarly="false"
      />
      {hasMaxLength && (
        <div
          id={counterId}
          style={{
            textAlign: "right",
            marginTop: "4px",
            fontSize: "12px",
            color: "#666",
          }}
        >
          <span>
            {charsTyped}/{maxLength}
            {charsTyped === maxLength && (
              <span className="text-danger" style={{ marginLeft: "8px" }}>
                Limit reached
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  );
};

export default CustomTextareaWidget;